import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, Chip, Button, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Avatar,
  Divider, CircularProgress, Switch, Snackbar, Alert,
  InputAdornment, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, Grid, CardContent, TableContainer, TablePagination, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Search, Close, Visibility, Phone, LocationOn, Add, Refresh,
} from '@mui/icons-material';
import { InvokeApi, apiList } from '../services/ApiService';
import { Config } from '../config/Config';

const kycColor = { pending: 'warning', approved: 'success', rejected: 'error', review: 'info' };

export default function Users() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  // Add User Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', email: '', address: '', device_id: '', kyc_status: 'pending' });
  const [addSaving, setAddSaving] = useState(false);

  // ── Fetch user list ─────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const res = await InvokeApi(Config.base_url + apiList.getUsers, {
      page: 1,
      per_page: 1500, // Increased to 1500 for all users
      search: '',
      status: statusFilter, // Pass status filter to backend
    });
    if (res?.code === '200') {
      const userList = (res.users ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 }));
      setUsers(userList);
      setTotal(res.total ?? res.users?.length ?? 0);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Apply local filters (search + kyc status) ────────────────────────────
  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      users.filter((u) => {
        const matchSearch =
          !q ||
          u.name?.toLowerCase().includes(q) ||
          u.phone?.includes(q) ||
          String(u.id).includes(q);
        const matchKyc = !kycFilter || u.kyc_status === kycFilter;
        return matchSearch && matchKyc;
      })
    );
    setPage(0);
  }, [search, kycFilter, users]);

  // ── Navigate to user detail page ─────────────────────────────────────────────
  const handleView = (row) => {
    navigate(`/user/${row.id}`);
  };

  // ── Toggle active ───────────────────────────────────────────────────────────
  const handleToggle = async (row) => {
    const res = await InvokeApi(Config.base_url + apiList.toggleUser, {
      id: row.id,
      active: !row.is_active,
    });
    if (res?.code === '200') {
      setSnack({ open: true, msg: 'User status updated.', sev: 'success' });
      fetchUsers();
    } else {
      setSnack({
        open: true,
        msg: res?.message || 'Toggle failed.',
        sev: 'error',
      });
    }
  };

  // ── Add User Handlers ─────────────────────────────────────────────────────────
  const handleAddUser = () => {
    setAddForm({ name: '', phone: '', email: '', address: '', device_id: '', kyc_status: 'pending' });
    setAddDialogOpen(true);
  };

  const handleAddSave = async () => {
    if (!addForm.name?.trim()) {
      setSnack({ open: true, msg: 'Name is required', sev: 'error' });
      return;
    }
    if (!addForm.phone?.trim()) {
      setSnack({ open: true, msg: 'Phone is required', sev: 'error' });
      return;
    }

    setAddSaving(true);
    const data = await InvokeApi(Config.base_url + apiList.createUser, addForm);
    setAddSaving(false);

    if (data?.code === '200') {
      setSnack({ open: true, msg: 'User created successfully', sev: 'success' });
      setAddDialogOpen(false);
      fetchUsers();
    } else {
      setSnack({ open: true, msg: data?.message || 'Failed to create user', sev: 'error' });
    }
  };

  const af = (key) => (e) => setAddForm(prev => ({ ...prev, [key]: e.target.value }));

  // Get paginated data
  const paginated = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Users</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} user{filtered.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={handleAddUser}
            sx={{ 
              background: 'linear-gradient(135deg,#1a237e,#1565c0)', 
              borderRadius: 2, 
              fontWeight: 700 
            }}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* ── Filters ──────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search ID, name, phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>User Status</InputLabel>
            <Select
              value={statusFilter}
              label="User Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>KYC Status</InputLabel>
            <Select
              value={kycFilter}
              label="KYC Status"
              onChange={(e) => {
                setKycFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="review">Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh">
            <IconButton 
              onClick={() => fetchUsers()} 
              sx={{ 
                color: '#1a237e', 
                border: '1px solid #e8eaf6', 
                borderRadius: 2 
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      {/* ── Table ────────────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['ID', 'User', 'Phone', 'KYC Status', 'Vehicle', 'Created', 'Active', 'Actions'].map(h => (
                      <TableCell 
                        key={h} 
                        sx={{ 
                          fontWeight: 700, 
                          color: '#1a237e', 
                          fontSize: 13, 
                          whiteSpace: 'nowrap' 
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Chip 
                            label={`#${user.id}`} 
                            size="small" 
                            sx={{ 
                              background: '#e8eaf6', 
                              color: '#1a237e', 
                              fontWeight: 700, 
                              fontSize: 12 
                            }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 34,
                                height: 34,
                                background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                                color: '#fff',
                                fontWeight: 700,
                                fontSize: 13,
                              }}
                            >
                              {(user.name || 'U')[0].toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600} fontSize={13}>
                                {user.name || 'Unknown'}
                              </Typography>
                              <Typography fontSize={11} color="text.secondary">
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {user.phone || '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.kyc_status || 'N/A'}
                            size="small"
                            color={kycColor[user.kyc_status] || 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {user.vehicle_number ? (
                            <Chip
                              label={user.vehicle_number}
                              variant="outlined"
                              color="primary"
                              size="small"
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {user.created_at
                            ? new Date(user.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Switch
                            size="small"
                            checked={!!user.is_active}
                            onChange={() => handleToggle(user)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#1a237e',
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(user)}
                              sx={{ color: '#1a237e' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add New User
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Name *"
                value={addForm.name}
                onChange={af('name')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Phone *"
                value={addForm.phone}
                onChange={af('phone')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                type="email"
                value={addForm.email}
                onChange={af('email')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Address"
                value={addForm.address}
                onChange={af('address')}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Device ID"
                value={addForm.device_id}
                onChange={af('device_id')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                select
                label="KYC Status"
                value={addForm.kyc_status}
                onChange={af('kyc_status')}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="review">In Review</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} sx={{ fontWeight: 700, color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSave}
            disabled={addSaving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', fontWeight: 700 }}
          >
            {addSaving ? <CircularProgress size={20} color="inherit" /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>
    <CircularProgress size={32} />
  </Box>
);

const EmptyState = ({ icon, text }) => (
  <Box sx={{ textAlign: 'center', py: 6, color: '#94a3b8' }}>
    {icon}
    <Typography sx={{ mt: 1 }}>{text}</Typography>
  </Box>
);
